import type { MenuItem } from "@replo/design-system/components/menu/Menu";
import type { ReploElementMetadata } from "schemas/generated/element";

import * as React from "react";

import LogoBadge from "@common/LogoBadge";
import { DEBUG_PANEL_WIDTH } from "@components/editor/constants";
import PublishedStatus from "@components/header/PublishedStatus";
import {
  getMenuItemsForElementItems,
  useFolders,
  usePageModificationHandlers,
} from "@components/left-bar/ElementsPane";
import Input from "@editor/components/common/designSystem/Input";
import { useIsDebugMode } from "@editor/components/editor/debug/useIsDebugMode";
import { useElementName } from "@editor/hooks/element/useElementName";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useDraftElementMetadata } from "@editor/hooks/useDraftElementMetadata";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import {
  selectIsRightBarAnalyticsOpen,
  selectLeftBarWidth,
  setIsRightBarAnalyticsOpen,
  setLeftBarActiveTab,
} from "@editor/reducers/ui-reducer";
import { useEditorDispatch, useEditorSelector } from "@editor/store";
import { docs } from "@editor/utils/docs";
import { canRenameElement } from "@editor/utils/element";
import { routes } from "@editor/utils/router";
import {
  selectInternalDebugModeOn,
  setEditorMode,
} from "@reducers/core-reducer";
import { selectLeftBarActiveTab } from "@reducers/ui-reducer";

import {
  LEFT_BAR_MENU_WIDTH,
  PANEL_PADDING,
} from "@/features/canvas/canvas-constants";
import { toast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Menu, MenuTrigger } from "@replo/design-system/components/menu/Menu";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { ChartLine, ChevronDown, Settings } from "lucide-react";
import { generatePath, Link } from "react-router-dom";

const ElementName = ({ element }: { element: ReploElementMetadata }) => {
  const leftBarWidth = useEditorSelector(selectLeftBarWidth);
  // Note (Ben O., 2025-04-18): These values are calculated based on
  // the width of: PublishedStatus, the IconButtons, CommandMenu, and the gaps & padding.
  const maxWidth = leftBarWidth - (element.isPublished ? 150 : 118);

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const { folders: allFolders } = useFolders();
  const currentFolder =
    allFolders.find((folder) => folder.id === element?.folderId) ?? null;
  const {
    handleDeletion,
    handleDuplication,
    handleEditPage,
    handleMoveToFolder,
    handleSupportDuplication,
    handleArchive,
  } = usePageModificationHandlers();
  const isDebugMode = useIsDebugMode();
  const dispatch = useEditorDispatch();
  const handleOpenVersionHistory = React.useCallback(() => {
    dispatch(setEditorMode("versioning"));
    dispatch(setLeftBarActiveTab("versionHistory"));
  }, [dispatch]);

  const menuItems: MenuItem[] = element
    ? getMenuItemsForElementItems({
        element,
        currentFolder,
        allFolders,
        handleDeletion,
        handleDuplication,
        handleSupportDuplication,
        handleEditPage,
        handleMoveToFolder,
        isDebugMode,
        handleArchive,
        handleVersionHistory: handleOpenVersionHistory,
      })
    : [];

  const {
    elementName,
    isEditingName,
    setIsEditingName,
    setElementName,
    handleKeyDown,
    isInvalidElementName,
    handleBlur,
  } = useElementName({
    element,
  });

  const handleTitleClick = () => {
    if (canRenameElement(element)) {
      setIsEditingName(true);
    } else {
      toast({
        header: "Cannot rename section",
        message:
          "This section cannot be renamed because it has been published.",
        type: "warning",
        cta: "Learn More",
        ctaOnClick: () => {
          window.open(docs.warnings.cannotRenameSection, "_blank");
        },
      });
    }
  };

  return (
    <div className="flex items-center gap-1 overflow-hidden">
      <PublishedStatus />

      {isEditingName ? (
        <div style={{ width: `${maxWidth}px` }} className="p-[1px]">
          <Input
            autoFocus
            value={elementName}
            onChange={(e) => setElementName(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            validityState={isInvalidElementName ? "invalid" : "valid"}
            size="base"
            unsafe_className="h-[30px]"
          />
        </div>
      ) : (
        <div className="flex flex-row">
          <Button
            variant="tertiary"
            size="base"
            tooltipText={elementName}
            UNSAFE_className={twMerge(
              `rounded-tr-none rounded-br-none border-r-0 max-sm:h-8 px-1 hover:bg-light-sheet`,
            )}
            id={element.id}
            disabled={false}
            isLoading={false}
            onClick={handleTitleClick}
            data-testid="element-page-title"
          >
            <div
              className="header-h4 overflow-hidden truncate"
              style={{ maxWidth: `${maxWidth}px` }}
            >
              {elementName}
            </div>
          </Button>
          <Menu
            items={menuItems}
            isOpen={isMenuOpen}
            onRequestClose={() => setIsMenuOpen(false)}
            contentClassNames="mt-1 z-max"
            trigger={
              <MenuTrigger asChild className="bg-transparent">
                <IconButton
                  variant="tertiary"
                  size="base"
                  onClick={() => setIsMenuOpen(true)}
                  data-testid="element-page-title-dropdown-button"
                  UNSAFE_className={twMerge(
                    "rounded-tl-none rounded-bl-none border-l-0",
                    isMenuOpen && "bg-light-surface",
                  )}
                  icon={<ChevronDown size={10} />}
                />
              </MenuTrigger>
            }
          />
        </div>
      )}
    </div>
  );
};

const ElementInformation = () => {
  const element = useDraftElementMetadata();
  const dispatch = useEditorDispatch();
  const logEvent = useLogAnalytics();
  const isPublished = element?.isPublished ?? false;
  const leftBarActiveTab = useEditorSelector(selectLeftBarActiveTab);
  const isRightBarAnalyticsOpen = useEditorSelector(
    selectIsRightBarAnalyticsOpen,
  );

  const handleOpenAnalyticsMenu = () => {
    if (isRightBarAnalyticsOpen) {
      dispatch(setIsRightBarAnalyticsOpen(false));
    } else {
      dispatch(setIsRightBarAnalyticsOpen(true));
      logEvent("analytics.editor.menu.open", {});
    }
  };

  if (!element) {
    return (
      <div className="flex w-full items-center gap-2">
        <Skeleton className="h-6 w-32" />
        <div className="ml-auto flex items-center gap-2">
          <Skeleton className="h-8 w-8 rounded-md" />
          <Skeleton className="h-8 w-8 rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full items-center justify-between gap-2">
      <ElementName element={element} />

      <div className="flex items-center gap-2">
        {isPublished && (
          <IconButton
            icon={<ChartLine size={16} />}
            tooltipText="Open Page Analytics"
            variant="secondary"
            UNSAFE_className={twMerge(
              "shadow-[0px_0px_4px_2px_rgba(0,0,0,0.02)] rounded-md bg-white",
              isRightBarAnalyticsOpen &&
                "bg-default text-white hover:bg-default",
            )}
            size="base"
            onClick={handleOpenAnalyticsMenu}
          />
        )}
        <IconButton
          variant="secondary"
          UNSAFE_className={twMerge(
            "shadow-[0px_0px_4px_2px_rgba(0,0,0,0.02)] rounded-md bg-white",
            leftBarActiveTab === "settings" &&
              "bg-default text-white hover:bg-default",
          )}
          aria-label="Open Page Settings"
          onClick={() => {
            if (leftBarActiveTab === "settings") {
              dispatch(setLeftBarActiveTab(null));
            } else {
              dispatch(setLeftBarActiveTab("settings"));
            }
          }}
          icon={<Settings size={16} />}
        />
      </div>
    </div>
  );
};

const LeftBarHeader = ({
  showElementSpecificContent,
  isFullPageOverlay,
}: {
  showElementSpecificContent: boolean;
  isFullPageOverlay: boolean;
}) => {
  const projectId = useCurrentProjectId();
  const isDebugPanelVisible = useEditorSelector(selectInternalDebugModeOn);
  const leftBarWidth = useEditorSelector(selectLeftBarWidth);

  return (
    <div
      className={twMerge(
        "shadow-panel border-0.5 border-secondary absolute top-2 flex items-center gap-3 rounded-xl bg-white p-3",
      )}
      style={{
        left: isDebugPanelVisible
          ? `${DEBUG_PANEL_WIDTH + PANEL_PADDING}px`
          : PANEL_PADDING,
        width: !isFullPageOverlay
          ? leftBarWidth + LEFT_BAR_MENU_WIDTH
          : undefined,
      }}
    >
      {/* Replo Icon */}
      <div className="text-secondary hover:text-muted flex cursor-pointer items-center transition-all">
        <Tooltip triggerAsChild key="logo" content="Back to Replo Dashboard">
          <Link
            to={generatePath(routes.shops.site, { projectId })}
            className="flex flex-row items-center"
          >
            <span className="sr-only">Back to Replo Dashboard</span>
            <LogoBadge
              aria-hidden
              className="h-8 w-8 cursor-pointer text-black"
            />
          </Link>
        </Tooltip>
      </div>

      {showElementSpecificContent && !isFullPageOverlay && (
        <div className="bg-border h-[16px] w-[1px]" />
      )}

      {showElementSpecificContent && !isFullPageOverlay && (
        <ElementInformation />
      )}
    </div>
  );
};

export default LeftBarHeader;
